// Section Helper Functions
// Utility functions for extracting metadata and creating section components

import {
  createFaq,
  updateFaq,
} from "@/api/admin/faq";
import {
  createVisionaries,
  updateVisionaries,
} from "@/api/admin/visionaries";
import {
  createLearnFromLeaders,
  updateLearnFromLeaders,
} from "@/api/admin/learnFromLeaders";
import {
  createProgramStructure,
  updateProgramStructure,
} from "@/api/admin/programStructure";
import {
  createElective,
  updateElective,
} from "@/api/admin/elective";
import {
  createFooter,
  updateFooter,
} from "@/api/admin/footer";
import {
  createProgramCards,
  updateProgramCards,
} from "@/api/admin/programCards";

// Helper to extract all METADATA_FIELDS_* from constants
export const extractMetadataFields = (constants: any) => {
  const metadataFields: any[] = [];
  const defaultRows: any[] = [];
  
  // Get custom metadata titles if available
  const metadataTitles = constants.METADATA_TITLES || {};
  // Optional per-group row-count limits, e.g. { metadata: { min: 2, max: 10 } }
  const metadataLimits = constants.METADATA_LIMITS || {};

  // Check for METADATA_FIELDS (primary)
  if (constants.METADATA_FIELDS && constants.METADATA_FIELDS.length > 0) {
    metadataFields.push({
      fields: constants.METADATA_FIELDS,
      dataKey: "metadata",
      title: metadataTitles.metadata || "Items",
      icon: "📊",
      minRows: metadataLimits.metadata?.min,
      maxRows: metadataLimits.metadata?.max,
    });
  }

  // Check for DEFAULT_ROW (primary)
  if (constants.DEFAULT_ROW) {
    defaultRows.push(constants.DEFAULT_ROW);
  }

  // Look for METADATA_FIELDS_1, METADATA_FIELDS_2, etc.
  Object.keys(constants).forEach((key) => {
    const match = key.match(/^METADATA_FIELDS_(\d+)$/);
    if (match) {
      const index = parseInt(match[1]);
      const dataKey = `metadata_${index}`;
      metadataFields.push({
        fields: constants[key],
        dataKey: dataKey,
        title: metadataTitles[dataKey] || (index === 1 ? "Items" : `Items ${index}`),
        icon: index === 1 ? "👥" : "📊",
        minRows: metadataLimits[dataKey]?.min,
        maxRows: metadataLimits[dataKey]?.max,
      });
    }
  });

  // Look for DEFAULT_ROW_1, DEFAULT_ROW_2, etc.
  Object.keys(constants).forEach((key) => {
    const match = key.match(/^DEFAULT_ROW_(\d+)$/);
    if (match) {
      const index = parseInt(match[1]);
      defaultRows[index] = constants[key];
    }
  });

  return { metadataFields, defaultRows };
};

// FAQ variant 4 ("Explore more" cards) supports an intentionally empty heading,
// so it must NOT get the legacy "FAQ Section" fallback that variants 1–3 use.
const faqTitleFallback = (payload: any) =>
  payload.variant === "4" ? (payload.title ?? "") : payload.title || "FAQ Section";

// Helper function to create FAQ component
export const createFaqComponent = async (payload: any) => {
  // This creates the main FAQ component (title, status, variant)
  const response = await createFaq({
    ...payload,
    title: faqTitleFallback(payload),
    variant: payload.variant || "1",
  });
  return response;
};

export const updateFaqComponent = async (id: number, payload: any) => {
  // This updates the main FAQ component
  const response = await updateFaq(id, {
    ...payload,
    title: faqTitleFallback(payload),
    variant: payload.variant || "1",
  });
  return response;
};

export const createVisionariesComponent = async (payload: any) => {
  const response = await createVisionaries({
    ...payload,
    variant: payload.variant || "1",
  });
  return response;
};

export const updateVisionariesComponent = async (id: number, payload: any) => {
  const response = await updateVisionaries(id, {
    ...payload,
    variant: payload.variant || "1",
  });
  return response;
};

export const createLearnFromLeadersComponent = async (payload: any) => {
  const response = await createLearnFromLeaders({
    ...payload,
    variant: payload.variant || "1",
  });
  return response;
};

export const updateLearnFromLeadersComponent = async (id: number, payload: any) => {
  const response = await updateLearnFromLeaders(id, {
    ...payload,
    variant: payload.variant || "1",
  });
  return response;
};

export const createProgramStructureComponent = async (payload: any) => {
  // This creates the main Program Structure component (title, status, variant)
  const response = await createProgramStructure({
    ...payload,
    title: payload.title || "Program Structure",
    variant: payload.variant || "1",
  });
  return response;
};

export const updateProgramStructureComponent = async (id: number, payload: any) => {
  // This updates the main Program Structure component
  const response = await updateProgramStructure(id, {
    ...payload,
    title: payload.title || "Program Structure",
  });
  return response;
};

export const createProgramCardsComponent = async (payload: any) => {
  const response = await createProgramCards({
    ...payload,
    title: payload.title || payload.title_1 || "Programs Built for Every Stage",
    variant: payload.variant || "2",
  });
  return response;
};

export const updateProgramCardsComponent = async (id: number, payload: any) => {
  const response = await updateProgramCards(id, {
    ...payload,
    variant: payload.variant || "2",
  });
  return response;
};

export const createElectiveComponent = async (payload: any) => {
  // This creates the main Elective component (title, sub_title, status, variant)
  const response = await createElective({
    ...payload,
    title: payload.title || "Electives List",
    variant: payload.variant || "1",
  });
  return response;
};

export const updateElectiveComponent = async (id: number, payload: any) => {
  // This updates the main Elective component
  const response = await updateElective(id, {
    ...payload,
    title: payload.title || "Electives List",
  });
  return response;
};

// Helper function to create Footer component
export const createFooterComponent = async (payload: any) => {
  // This creates the main Footer component (title, status, variant)
  const response = await createFooter({
    ...payload,
    title: payload.title || "Footer Section",
    variant: payload.variant || "1",
  });
  return response;
};

export const updateFooterComponent = async (id: number, payload: any) => {
  // This updates the main Footer component
  const response = await updateFooter(id, {
    ...payload,
    title: payload.title || "Footer Section",
  });
  return response;
};

